import type { CreatureState, SimEvent } from '@battle/sim';
import * as THREE from 'three/webgpu';
import type { CreatureModel } from './creature-model.ts';
import { createLookAt, createSlopeAdapter, createSpringChain } from './procedural.ts';

/**
 * Presentation for one simulated creature: places the model at the interpolated sim state and
 * layers animation on top (docs/06 "Animation"): a speed-matched idle/walk/run blend, lean
 * into turns and acceleration, landing squash, and a dodge flourish. Never feeds back into
 * the sim.
 */
export interface ViewTuning {
  /** Ground speed (m/s) at which the walk and run clips play at 1x, so feet don't slide. */
  walkRef: number;
  runRef: number;
  leanPerYawRate: number; // radians of roll per rad/s of turning
  leanMax: number;
  pitchPerAccel: number;
  squash: number;
  /** Kilnback curls into a roll on dodge; others lunge. */
  dodgeRoll: boolean;
  /** Height of the roll's spin center above the feet (m). */
  rollPivot: number;
}

const pivot = new THREE.Vector3();
const rotated = new THREE.Vector3();

export const VIEW_TUNING: Record<string, ViewTuning> = {
  kilnback: {
    walkRef: 1.7,
    runRef: 5.2,
    leanPerYawRate: 0.05,
    leanMax: 0.2,
    pitchPerAccel: 0.006,
    squash: 0.12,
    dodgeRoll: true,
    rollPivot: 0.6,
  },
  rillwing: {
    walkRef: 1.9,
    runRef: 5.6,
    leanPerYawRate: 0.07,
    leanMax: 0.28,
    pitchPerAccel: 0.01,
    squash: 0.08,
    dodgeRoll: false,
    rollPivot: 1.1,
  },
};

/** Per-frame inputs the procedural layers need from the game. */
export interface ViewContext {
  /** World-space look direction for the head (camera aim or lock target), or null. */
  look: { yaw: number; pitch: number } | null;
  /** Ground height under (x, z), casting down from y; null if nothing is below. */
  ground: (x: number, y: number, z: number) => number | null;
}

const RIG: Record<
  string,
  { neck: string[]; lookWeights: number[]; tail: string[]; body: [number, number] }
> = {
  kilnback: {
    neck: ['neck', 'head'],
    lookWeights: [0.4, 0.6],
    tail: ['tail_01', 'tail_02', 'tail_03'],
    body: [0.8, 0.4],
  },
  rillwing: {
    neck: ['neck_01', 'neck_02', 'neck_03', 'neck_04', 'head'],
    lookWeights: [0.15, 0.15, 0.15, 0.2, 0.35],
    tail: ['tail_01', 'tail_02', 'tail_03'],
    body: [0.35, 0.25],
  },
};

export interface CreatureView {
  tuning: ViewTuning;
  readonly model: CreatureModel;
  /** alpha interpolates prev → curr sim states. */
  sync(
    prev: CreatureState,
    curr: CreatureState,
    alpha: number,
    dt: number,
    elapsed: number,
    ctx?: ViewContext,
  ): void;
  onEvent(e: SimEvent): void;
}

const smooth = (x: number, a: number, b: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

function lerpAngle(a: number, b: number, t: number) {
  const d = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + d * t;
}

export function createCreatureView(
  model: CreatureModel,
  id: string,
  dodgeTicks: number,
): CreatureView {
  const tuning = { ...(VIEW_TUNING[id] ?? (VIEW_TUNING.kilnback as ViewTuning)) };
  const idle = model.actions.get('idle');
  const walk = model.actions.get('walk');
  const run = model.actions.get('run');
  for (const a of [idle, walk, run]) {
    if (!a) continue;
    a.reset().play();
    a.setEffectiveWeight(a === idle ? 1 : 0);
  }
  const rig = RIG[id] ?? (RIG.kilnback as NonNullable<(typeof RIG)['kilnback']>);
  const bone = (n: string) => model.body.getObjectByName(n);
  const tail = createSpringChain(rig.tail.map(bone).filter((b): b is THREE.Object3D => !!b));
  const look = createLookAt(
    rig.neck.map(bone).filter((b): b is THREE.Object3D => !!b),
    {
      maxYaw: 1.0,
      maxPitch: 0.45,
      speed: 6,
      weights: rig.lookWeights,
    },
  );
  const slope = createSlopeAdapter(rig.body[0], rig.body[1]);
  // Procedural layers add to the clip pose. Bones a clip doesn't key keep last frame's value,
  // so restore their rest rotation first or the offsets accumulate (a spinning head).
  const layered = [...rig.neck, ...rig.tail].map(bone).filter((b): b is THREE.Object3D => !!b);
  const rest = layered.map((b) => b.quaternion.clone());
  let lastYaw: number | null = null;
  let roll = 0;
  let pitch = 0;
  let lastSpeed = 0;
  let squash = 0;
  let dodgeT = -1;
  let dodgeDir = new THREE.Vector2();

  return {
    tuning,
    model,
    onEvent(e) {
      if (e.type === 'land') squash = Math.min(1, e.impact / 12);
      if (e.type === 'dodge') {
        dodgeT = 0;
        dodgeDir = new THREE.Vector2(e.dirX, e.dirZ);
      }
    },
    sync(prev, curr, alpha, dt, elapsed, ctx) {
      const r = model.root;
      r.position.set(
        prev.pos.x + (curr.pos.x - prev.pos.x) * alpha,
        prev.pos.y + (curr.pos.y - prev.pos.y) * alpha,
        prev.pos.z + (curr.pos.z - prev.pos.z) * alpha,
      );
      const yaw = lerpAngle(prev.yaw, curr.yaw, alpha);
      r.rotation.y = yaw;

      // Locomotion blend by planar speed.
      const vx = prev.vel.x + (curr.vel.x - prev.vel.x) * alpha;
      const vz = prev.vel.z + (curr.vel.z - prev.vel.z) * alpha;
      const speed = curr.grounded || curr.mode === 'dodge' ? Math.hypot(vx, vz) : lastSpeed * 0.98;
      const moving = smooth(speed, 0.15, tuning.walkRef * 0.6);
      const toRun = smooth(speed, tuning.walkRef * 1.4, tuning.runRef * 0.9);
      idle?.setEffectiveWeight(1 - moving);
      walk?.setEffectiveWeight(moving * (1 - toRun));
      run?.setEffectiveWeight(moving * toRun);
      walk?.setEffectiveTimeScale(THREE.MathUtils.clamp(speed / tuning.walkRef, 0.6, 1.8));
      run?.setEffectiveTimeScale(THREE.MathUtils.clamp(speed / tuning.runRef, 0.7, 1.6));
      if (!curr.grounded && curr.mode !== 'dodge') run?.setEffectiveTimeScale(0.35);

      // Lean: roll into turns, pitch with acceleration.
      const yawRate =
        lastYaw === null || dt <= 0
          ? 0
          : Math.atan2(Math.sin(yaw - lastYaw), Math.cos(yaw - lastYaw)) / dt;
      lastYaw = yaw;
      const accel = dt > 0 ? (speed - lastSpeed) / dt : 0;
      lastSpeed = speed;
      const wantRoll = THREE.MathUtils.clamp(
        -yawRate * tuning.leanPerYawRate * Math.min(1, speed / tuning.runRef),
        -tuning.leanMax,
        tuning.leanMax,
      );
      const wantPitch = THREE.MathUtils.clamp(accel * tuning.pitchPerAccel, -0.12, 0.12);
      roll += (wantRoll - roll) * Math.min(1, dt * 10);
      pitch += (wantPitch - pitch) * Math.min(1, dt * 6);

      const b = model.body;
      const sl = ctx
        ? slope.update(dt, r.position, yaw, ctx.ground, curr.grounded)
        : { pitch: 0, roll: 0 };
      b.rotation.set(pitch + sl.pitch, 0, roll - sl.roll);
      b.position.set(0, 0, 0);
      b.scale.set(1, 1, 1);

      // Landing squash (decays), applied as a scale around the feet.
      if (squash > 0.001) {
        const s = squash * tuning.squash;
        b.scale.set(1 + s * 0.5, 1 - s, 1 + s * 0.5);
        squash = Math.max(0, squash - dt * 5);
      }

      // Dodge flourish over the dodge's active ticks.
      if (dodgeT >= 0) {
        dodgeT += dt;
        const k = dodgeT / (dodgeTicks / 60);
        if (k >= 1) dodgeT = -1;
        else if (tuning.dodgeRoll) {
          // Curl into a wheel: a full forward roll around the local lateral axis, lifted so the
          // shell clears the ground (the Kiln Roll silhouette from concept K-C).
          const ang = k * Math.PI * 2;
          const localDir = Math.atan2(dodgeDir.x, dodgeDir.y) - yaw;
          b.rotation.set(Math.cos(localDir) * ang + pitch, 0, -Math.sin(localDir) * ang + roll);
          // Spin around the body's center, not the feet, lifted so the shell clears the floor.
          pivot.set(0, tuning.rollPivot, 0);
          rotated.copy(pivot).applyEuler(b.rotation);
          b.position.copy(pivot).sub(rotated);
          b.position.y += Math.sin(k * Math.PI) * 0.35;
          const curl = Math.sin(k * Math.PI);
          b.scale.set(1 - 0.12 * curl, 1 - 0.1 * curl, 1 - 0.25 * curl);
          model.setHeat(1 + 1.5 * curl);
        } else {
          b.rotation.x = pitch + Math.sin(k * Math.PI) * 0.35;
          b.position.y = Math.sin(k * Math.PI) * 0.15;
        }
      } else {
        model.setHeat(curr.sprinting ? 1.35 : 1);
      }

      for (let i = 0; i < layered.length; i++)
        layered[i]?.quaternion.copy(rest[i] as THREE.Quaternion);
      model.update(dt, elapsed);

      // Procedural layers on top of the clip pose.
      model.root.updateMatrixWorld(true);
      const vy = prev.vel.y + (curr.vel.y - prev.vel.y) * alpha;
      tail.apply(dt, yawRate, curr.grounded ? 0 : vy, yaw);
      look.apply(dt, yaw, ctx?.look ?? null, curr.mode === 'dodge' ? 0 : 1);
    },
  };
}
